<script setup lang="ts">
/**
 * 通用单行文本输入弹窗：用于重命名、新建文件夹等轻量输入。
 * 打开后自动聚焦并全选文本，回车触发确定。
 */
import { nextTick, ref, watch } from 'vue'
import { NInput, NModal } from 'naive-ui'

const props = withDefaults(defineProps<{
  show: boolean
  modelValue: string
  title: string
  placeholder?: string
  positiveText?: string
  negativeText?: string
  confirm: (value: string) => boolean | void | Promise<boolean | void>
}>(), {
  placeholder: '请输入内容',
  positiveText: '确定',
  negativeText: '取消',
})

const emit = defineEmits<{
  'update:show': [value: boolean]
}>()

const draftValue = ref('')
const inputRef = ref<InstanceType<typeof NInput> | null>(null)

function focusAndSelectInput(): void {
  void nextTick(() => {
    inputRef.value?.focus()
    inputRef.value?.select()
  })
}

watch(
  () => props.show,
  (show) => {
    if (!show) return
    draftValue.value = props.modelValue
    focusAndSelectInput()
  },
)

async function handleConfirm(): Promise<boolean> {
  const result = await props.confirm(draftValue.value)
  if (result === false) {
    focusAndSelectInput()
    return false
  }
  emit('update:show', false)
  return true
}

function handleClose(): void {
  emit('update:show', false)
}
</script>

<template>
  <n-modal
    :show="show"
    preset="dialog"
    :title="title"
    :positive-text="positiveText"
    :negative-text="negativeText"
    @positive-click="handleConfirm"
    @negative-click="handleClose"
    @update:show="emit('update:show', $event)"
  >
    <n-input
      ref="inputRef"
      v-model:value="draftValue"
      size="small"
      :placeholder="placeholder"
      @keydown.enter.prevent="handleConfirm"
    />
  </n-modal>
</template>
